import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { PlusIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

/**
 * ProjectMissingItems component displays and manages missing items for a project
 */
const ProjectMissingItems = ({ projectId }) => {
  const [missingItems, setMissingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    fetchMissingItems();
  }, [projectId]);
  
  const fetchMissingItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/missing-items/project/${projectId}`);
      
      if (response.data && response.data.missing_items) {
        setMissingItems(response.data.missing_items);
      } else {
        setMissingItems([]);
      }
    } catch (err) {
      console.error('Error fetching missing items:', err);
      setError('Failed to load missing items. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!newItem.trim()) {
      toast.error('Item description cannot be empty');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const response = await api.post(`/missing-items/project/${projectId}`, {
        content: newItem,
        is_internal: isInternal,
        due_date: dueDate || null
      });
      
      if (response.data && response.data.missing_item) {
        // Add the new item to the list
        setMissingItems([response.data.missing_item, ...missingItems]);
        
        // Reset form
        setNewItem('');
        setDueDate('');
        setShowAddForm(false);
        
        toast.success('Missing item added successfully');
      }
    } catch (err) {
      console.error('Error adding missing item:', err);
      toast.error('Failed to add missing item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api.put(`/missing-items/${id}`, {
        status: newStatus
      });
      
      if (response.data && response.data.missing_item) {
        // Update the item in the list
        setMissingItems(missingItems.map(item => 
          item.id === id ? response.data.missing_item : item
        ));
        
        toast.success(`Item marked as ${newStatus}`);
      }
    } catch (err) {
      console.error('Error updating missing item status:', err);
      toast.error('Failed to update status. Please try again.');
    }
  };
  
  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      await api.delete(`/missing-items/${id}`);
      
      // Remove the item from the list
      setMissingItems(missingItems.filter(item => item.id !== id));
      
      toast.success('Item deleted successfully');
    } catch (err) {
      console.error('Error deleting missing item:', err);
      toast.error('Failed to delete item. Please try again.');
    }
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Calculate if an item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };
  
  return (
    <div className="space-y-4 missing-info-container">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Missing Information</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Missing Item
        </button>
      </div>
      
      {/* Add form */}
      {showAddForm && (
        <div className="bg-white shadow-sm rounded-md p-4 border border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">Add Missing Item</h4>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div>
              <label htmlFor="newItem" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="newItem"
                name="newItem"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                placeholder="Enter description of missing information..."
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                  Due Date (Optional)
                </label>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
              
              <div className="flex items-center h-full pt-6">
                <input
                  id="isInternal"
                  name="isInternal"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                <label htmlFor="isInternal" className="ml-2 block text-sm text-gray-700">
                  Internal Only (not visible to client)
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isSubmitting ? 'Adding...' : 'Add Item'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-8 h-8 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Missing items list */}
      {!loading && !error && missingItems.length === 0 && (
        <div className="bg-gray-50 p-4 text-center rounded-md border border-gray-200">
          <p className="text-gray-500">No missing items added yet.</p>
        </div>
      )}
      
      {missingItems.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {missingItems.map((item) => (
              <li key={item.id} className={`${
                item.status === 'received' ? 'bg-green-50' : 
                isOverdue(item.due_date) && item.status === 'pending' ? 'bg-red-50' : ''
              }`}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        item.status === 'received' ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}>
                        {item.content}
                      </p>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <span className="inline-flex items-center mr-4 text-gray-900">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          {formatDate(item.due_date)}
                          {isOverdue(item.due_date) && item.status === 'pending' && (
                            <span className="ml-2 text-xs font-semibold text-red-600">
                              OVERDUE
                            </span>
                          )}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.is_internal ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {item.is_internal ? 'Internal' : 'Client-Visible'}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          Added by {item.created_by_name || 'Team Member'} on {formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      {item.status === 'pending' ? (
                        <button
                          onClick={() => handleStatusChange(item.id, 'received')}
                          className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          title="Mark as Received"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatusChange(item.id, 'pending')}
                          className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                          title="Mark as Pending"
                        >
                          <ClockIcon className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Delete Item"
                      >
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProjectMissingItems; 